import { Link } from "react-router-dom";
import { Flame, Package, CalendarSync, Home, HeartHandshake, MessageSquareQuote, ArrowRight } from "lucide-react";
import { Reveal, Section, SectionHeading } from "./Section";
import { PostcodeChecker } from "./PostcodeChecker";
import { useZones } from "@/lib/api";

const REASONS = [
  { icon: Flame, title: "Kiln-dried for an easier light", text: "Kiln-dried logs light more easily and burn cleaner than damp or unseasoned wood." },
  { icon: Package, title: "Everything to get it going", text: "Kindling and firelighters come with every plan, so you're never caught short." },
  { icon: CalendarSync, title: "Flexible by design", text: "Pause for up to three months, skip a delivery, change plan or cancel — all online." },
  { icon: Home, title: "Delivered to your door", text: "No more trips to the garage forecourt or heavy bags in the car boot." },
];

export const WhyChoose = () => (
  <Section className="bg-[#0b0b0b]" data-testid="why-choose-section">
    <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
      <div className="lg:col-span-5">
        <SectionHeading eyebrow="Why WeeLogRun" title="Why choose WeeLogRun?" intro="A real fire should be the easy part of your evening. We built WeeLogRun to make it exactly that." />
        <Reveal delay={0.1} className="mt-10 overflow-hidden rounded-3xl border border-[#262626]">
          <img src="/img/logs-split.jpg" alt="Close-up of split firewood logs stacked together" className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
        </Reveal>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7 lg:pt-24">
        {REASONS.map(({ icon: Icon, title, text }, i) => (
          <Reveal key={title} delay={i * 0.08} className={i % 2 ? "sm:translate-y-10" : ""}>
            <div className="h-full rounded-3xl border border-[#262626] bg-[#121212] p-7 transition-[border-color] duration-300 hover:border-[#D4A437]/40">
              <Icon className="h-7 w-7 text-[#FF6A00]" />
              <h3 className="mt-6 font-display text-xl font-semibold">{title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[#A1A1AA]">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

export const StorySection = () => (
  <Section data-testid="story-section">
    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
      <Reveal className="relative">
        <div className="overflow-hidden rounded-[2rem] border border-[#262626]">
          <img src="/img/logs-stack.jpg" alt="A neat stack of split firewood" className="h-[420px] w-full object-cover sm:h-[520px]" loading="lazy" />
        </div>
        <div className="absolute -bottom-6 right-6 flex items-center gap-3 rounded-2xl border border-[#D4A437]/30 bg-[#0d0d0d]/95 px-5 py-4 backdrop-blur-xl">
          <HeartHandshake className="h-6 w-6 text-[#D4A437]" />
          <p className="text-sm font-semibold">Local to Northern Ireland</p>
        </div>
      </Reveal>
      <div>
        <SectionHeading eyebrow="Our story" title="The WeeLogRun story." />
        <Reveal delay={0.1} className="mt-6 space-y-4 text-base leading-relaxed text-[#F7F5F0]/80">
          <p>WeeLogRun is your local log membership delivery team for Newtownabbey and the surrounding areas.</p>
          <p>We started WeeLogRun for households who love a real fire but are tired of running out at the worst possible moment. One simple monthly plan, delivered to your door, with the kindling and firelighters you need to get it going.</p>
          <p>Quality logs from a local team — just a far easier way to keep the stove going all season.</p>
        </Reveal>
        <Reveal delay={0.2}>
          <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#FF6A00] hover:gap-3 transition-[gap]" data-testid="story-about-link">
            Read more about us <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
    </div>
  </Section>
);

export const ZoneList = () => {
  const { data } = useZones();
  return (
    <ul className="grid gap-3 sm:grid-cols-2" data-testid="zone-list">
      {(data?.zones || []).map((z) => (
        <li key={z.district} className="flex items-center gap-4 rounded-2xl border border-[#262626] bg-[#111] p-4">
          <span className="font-display text-2xl font-bold text-[#FF6A00]">{z.district}</span>
          <span className="text-sm text-[#F7F5F0]/80">{z.name}</span>
        </li>
      ))}
    </ul>
  );
};

export const DeliveryAreasSection = () => (
  <Section id="delivery" className="bg-[#0b0b0b]" data-testid="delivery-areas-section">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeading eyebrow="Delivery areas" title="Newtownabbey & surrounding areas." intro="We currently deliver to BT36, BT37, BT38 and BT39. Enter your full postcode to check." />
      </div>
      <Reveal delay={0.1} className="lg:col-span-7 space-y-8">
        <div className="rounded-3xl border border-[#262626] bg-[#111] p-6 sm:p-8">
          <PostcodeChecker idPrefix="home-postcode" />
        </div>
        <ZoneList />
      </Reveal>
    </div>
  </Section>
);

export const Testimonials = () => (
  <Section data-testid="testimonials-section">
    <SectionHeading eyebrow="Member reviews" title="Reviews from real WeeLogRun members." />
    <Reveal delay={0.1} className="mt-12 flex flex-col items-start gap-5 rounded-3xl border border-dashed border-[#333] bg-[#0e0e0e] p-8 sm:flex-row sm:items-center sm:p-10" data-testid="testimonials-empty">
      <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-[#D4A437]/10 text-[#D4A437]">
        <MessageSquareQuote className="h-6 w-6" />
      </span>
      <div>
        <p className="font-display text-xl font-semibold">Our first members' reviews are on their way.</p>
        <p className="mt-2 max-w-2xl text-[15px] text-[#A1A1AA]">We only publish genuine, approved reviews from WeeLogRun members after their deliveries — never invented testimonials.</p>
      </div>
    </Reveal>
  </Section>
);

export const FinalCTA = () => (
  <section className="relative overflow-hidden" data-testid="final-cta">
    <img src="/img/fireplace-dark.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
    <div className="absolute inset-0 bg-[#080808]/75" aria-hidden="true" />
    <div className="relative lc-container py-28 sm:py-36">
      <Reveal className="max-w-2xl">
        <p className="lc-eyebrow"><span className="h-px w-6 bg-[#D4A437]/70" />Join WeeLogRun</p>
        <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
          Never Run Out of Logs <span className="text-[#FF6A00]">Again</span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#F7F5F0]/80">Choose a plan in minutes. We'll keep the logs coming, month after month.</p>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row">
          <Link to="/plans" className="lc-btn-primary !px-8 !py-4 text-base" data-testid="final-cta-plans">Explore Our Plans</Link>
          <Link to="/delivery-areas#check" className="lc-btn-ghost !px-8 !py-4 text-base" data-testid="final-cta-postcode">Check your postcode</Link>
        </div>
      </Reveal>
    </div>
  </section>
);
